import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import NotFound from "@/pages/NotFound.tsx";
import { TimeOffHistoryReportTable } from "@/components/time-off/components/TimeOffHistoryReportTable.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useTimeOffHook from "@/hooks/use-time-off.hook.tsx";
import {
  useEmployeeDetails,
  useEmployeeTimeOffCategoriesData,
  useEmployeeTimeOffRequests,
} from "@/queries/employees/use-employees.query.ts";
import { ApiAnalyticsTimeOffLeaveUsedFilter } from "@/types/analytics/analytics.time-off.types.ts";
import { ApiEmployeeTimeOffCategoryPayload } from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { ApiTimeOffLogType } from "@/types/time-off/time-off.types.ts";
import {
  Button,
  CoreFilterType,
  FilterRenderType,
  FilterValueType,
  Page,
  PaginationBlock,
  Stack,
  TabButton,
  TabList,
} from "@skailer-com/uikit";
import { parseInt } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export function EmployeeTimeOffsHistoryPage() {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { canIEdit, canViewField } = usePermissions();
  const { openAdjustmentBalance, getActionTypeTitle } = useTimeOffHook();
  const { id: employeeId, category_id } = useParams();
  const [, setSearchParams] = useSearchParams();

  const [payloadCategory] = useState<ApiEmployeeTimeOffCategoryPayload>({
    date_balance: new Date(),
  });
  const { data: employee } = useEmployeeDetails(employeeId ? parseInt(employeeId, 10) : undefined);
  const canViewTimeOff =
    canIEdit(ApiPermission.manage_leave_policies) ||
    canViewField(employee, EmployeeFieldPermission.employee_time_off);
  const { data: employeeTimeOffCategories } = useEmployeeTimeOffCategoriesData(
    Number(employeeId),
    payloadCategory,
    canViewTimeOff
  );

  const navigate = useNavigate();

  const handleTabChanged = (tabIndex: number) => {
    const currentSearchParams = new URLSearchParams(window.location.search);

    if (tabIndex === 0)
      navigate(`/employees/${employeeId}/time-off-history?` + currentSearchParams);

    const category = employeeTimeOffCategories?.[tabIndex - 1];
    if (category)
      navigate(`/employees/${employeeId}/time-off-history/${category.id}?` + currentSearchParams);
  };

  const activeCategoryId = useMemo(() => {
    if (!category_id) return undefined;
    return Number(category_id);
  }, [category_id]);

  const library = filtersLibrary();
  const {
    filter: { filter: filter },
    filterButton,
  } = useAppFilter<ApiAnalyticsTimeOffLeaveUsedFilter>({
    filters: [
      library.page_number,
      library.category_ids,
      {
        name: "category_id",
        type: CoreFilterType.param,
        searchParamName: "category_id",
        valueType: FilterValueType.number,
        renderType: FilterRenderType.none,
      },
      library.date_from,
      library.date_to,
      library.request_statuses,
      {
        name: "action_types",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.multiselect,
        label: t("common:type"),
        options: [
          ApiTimeOffLogType.accrual,
          ApiTimeOffLogType.used,
          ApiTimeOffLogType.canceled,
          ApiTimeOffLogType.adjustment,
          ApiTimeOffLogType.carryover,
          ApiTimeOffLogType.initial_balance,
          ApiTimeOffLogType.balance_import,
        ].map((x) => ({
          id: x,
          title: getActionTypeTitle(x),
        })),
      },
    ],
    source: "Employee Time Off",
  });

  const { data: timeOffRequests, isLoading: isRequestsLoading } = useEmployeeTimeOffRequests(
    Number(employeeId),
    filter,
    canViewTimeOff
  );

  if (employee && !canViewTimeOff) return <NotFound />;

  return (
    <Page className="h-full">
      <Page.Header
        title={t("time_off:time_off_history_for", {
          employee: employee ? renderEmployeeName(employee) : "",
        })}
        showBack={true}
        navigateTo={`/employees/${employeeId}/time-off`}
      >
        <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
          {filterButton()}
          {canIEdit(ApiPermission.manage_leave_policies) && (
            <Button
              variant="secondary"
              onClick={() => {
                if (employee) openAdjustmentBalance(employee);
              }}
            >
              {t("time_off:edit_balance")}
            </Button>
          )}
        </Stack>
      </Page.Header>
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <TabList>
              <TabButton
                title={t("time_off:all_timeoff")}
                isActive={activeCategoryId == undefined}
                onClick={() => {
                  handleTabChanged(0);
                }}
              />
              {employeeTimeOffCategories?.map((category, index) => (
                <TabButton
                  key={category.id}
                  title={category.title}
                  isActive={activeCategoryId === category.id}
                  onClick={() => {
                    handleTabChanged(index + 1);
                  }}
                />
              ))}
            </TabList>
          }
          footer={
            <PaginationBlock
              metadata={timeOffRequests?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <TimeOffHistoryReportTable
            logs={timeOffRequests?.items || []}
            isLoading={isRequestsLoading}
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
